"use client";
import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChartWithLabel } from "@/type/financialPageTypes";
import { useIsMobile } from "@/utils/useIsMobile";
import Icon from "@/components/icons/Icons";

interface Props {
  onClose: () => void;
}

type CorpusCategory = {
  label: string;
  percent: number;
  legendPercent: string;
  color: string;
  description: string;
};

const CATEGORIES: CorpusCategory[] = [
  {
    label: "Pension",
    percent: 16.9,
    legendPercent: "6",
    color: "#FDE006",
    description: `A significant portion of your retirement corpus is dedicated to generating a reliable monthly income throughout your retirement.
        <br/><br/>
      The Pension Bucket remains invested while a Systematic Withdrawal Plan (SWP) provides a regular monthly payout, helping you manage your day-to-day expenses and maintain your desired lifestyle with confidence.`,
  },
  {
    label: "Healthcare Protection",
    percent: 16.9,
    legendPercent: "6",
    color: "#79FD06",
    description: `A dedicated Healthcare Protection Bucket is created to help manage future medical expenses and unexpected healthcare emergencies.
        <br/><br/>
      This corpus remains invested to continue growing over time while providing financial security for hospitalization, treatments, insurance premiums, and other healthcare needs during retirement.`,
  },
  {
    label: "Lifestyle & Travel",
    percent: 28.2,
    legendPercent: "10",
    color: "#04BCFF",
    description: `Retirement is about more than financial security—it's about enjoying the freedom you've worked hard to achieve.
        <br/><br/>
      The Lifestyle & Travel Bucket is designed to fund vacations, hobbies, celebrations, and personal aspirations, allowing you to enjoy retirement without compromising your long-term financial goals.`,
  },
  {
    label: "Family Legacy",
    percent: 38,
    legendPercent: "12-15",
    color: "#FF9304",
    description: `The Family Legacy Bucket is designed to preserve and grow a portion of your retirement wealth for future generations.
        <br/><br/>
      Remaining invested in a long-term growth portfolio, this corpus has the potential to create meaningful wealth that can be passed on to your family while your other retirement needs are taken care of.`,
  },
];

export default function ScenarioBottomSheet({ onClose }: Props) {
  const isMobile = useIsMobile();

  const renderCenterLabel = (chart: ChartWithLabel) => {
    chart.customLabel?.destroy();

    const centerX = chart.plotWidth / 2 + chart.plotLeft;
    const centerY = chart.plotHeight / 2 + chart.plotTop;
    const fontSize = isMobile ? "11px" : "16px";

    const group = chart.renderer.g().add();

    chart.renderer
      .text("Your", centerX, centerY - (isMobile ? 2 : 4))
      .attr({ align: "center", zIndex: 5 })
      .css({ color: "#ffffff", fontSize, fontWeight: "700" })
      .add(group);

    chart.renderer
      .text("Corpus", centerX, centerY + (isMobile ? 12 : 18))
      .attr({ align: "center", zIndex: 5 })
      .css({ color: "#ffffff", fontSize, fontWeight: "700" })
      .add(group);

    chart.customLabel = group;
  };

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      width: isMobile ? 110 : 160,
      height: isMobile ? 110 : 160,
      spacing: [0, 0, 0, 0],
      events: {
        load() {
          renderCenterLabel(this as ChartWithLabel);
        },
        redraw() {
          renderCenterLabel(this as ChartWithLabel);
        },
      },
    },
    title: { text: undefined },
    tooltip: { enabled: false },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      pie: {
        enableMouseTracking: false,
        innerSize: isMobile ? "75%" : "65%",
        shadow: false,
        borderWidth: 8, // gap between each category slice
        borderColor: "transparent",
        borderRadius: 4,
        dataLabels: { enabled: false },
      },
    },
    series: [
      {
        type: "pie",
        data: CATEGORIES.map((category) => ({
          name: category.label,
          y: category.percent,
          color: category.color,
        })),
      },
    ],
  };

  return (
    <motion.div
      key="scenario-overlay"
      className="absolute inset-0 bg-black/60 backdrop-blur-md z-1 flex flex-col justify-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      {/* Sheet */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 280 }}
        onClick={(e) => e.stopPropagation()}
        className="relative overflow-y-auto scrollbar-none bg-white rounded-t-2xl sm:rounded-t-5xl max-h-[80%] sm:max-h-full w-[98%] mx-auto overflow-hidden"
      >
        {/* Header */}
        <div className="sticky top-0 z-2 px-5 py-3 flex items-center justify-between xs:justify-center gap-4 bg-[#003D6C] shrink-0">
          <p className="text-2xs sm:text-base text-center font-semibold text-white uppercase">
            Let&apos;s Understand Scenarios
          </p>

          <button
            onClick={onClose}
            className="w-4 h-4 sm:w-7 sm:h-7 xs:absolute inset-y-0 right-2.5 sm:right-4 my-auto transition text-white hover:scale-[1.03]"
          >
            <Icon.crossIcon />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:bg-[#EDF4FF] sm:border border-primaryBlue sm:rounded-6xl">
            {/* Left: Donut + Legend */}
            <div className="w-full sm:w-[45%] sm:p-5">
              <div className="flex flex-row sm:flex-col gap-1 rounded-3xl pl-0 p-3 xs:p-4 relative overflow-hidden">
                <Image
                  src="/next_assets/images/financial-planning/scenario.png"
                  alt="Background Image"
                  fill
                  priority
                  className="object-cover"
                />

                <div className="flex w-100 sm:w-full justify-center items-center shrink-0 relative z-1">
                  <HighchartsReact
                    key={isMobile ? "mobile" : "desktop"}
                    highcharts={Highcharts}
                    options={chartOptions}
                  />
                </div>

                <div className="grid gap-2.5 w-full sm:mt-4 relative z-1">
                  {CATEGORIES.map((category) => (
                    <div key={category.label} className="flex items-center gap-2 text-2xs sm:text-sm text-white">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: category.color }} />
                      <p>{category.label}</p>
                      <p className="ml-auto text-white font-semibold">{category.legendPercent}%</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Info */}
            <div className="space-y-3 sm:space-y-4 flex-1 sm:max-h-91 overflow-y-auto scrollbar-none sm:p-5 sm:pl-0 mt-4 sm:mt-0">
              <p className="sm:text-lg sm:leading-1.3 font-medium text-black">
                What you can achieve with our <span className="font-semibold">Retirement Planning</span>?
              </p>

              {CATEGORIES.map((category) => (
                <div key={category.label} className="bg-[#EDF4FF] sm:bg-white rounded-1.5xl p-3 xs:p-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: category.color }} />
                    <p className="text-black font-semibold">{category.label}</p>
                  </div>
                  <p
                    className="text-xs sm:text-2xs sm:leading-1.2 text-[#666666] mt-2"
                    dangerouslySetInnerHTML={{ __html: category.description }}
                  />
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-[#666666] text-center mt-4">
            <span className="font-semibold">Note:</span> Mutual fund investments are subjected to market risks. Results
            may differ with actual calculations and market scenarios.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
